<template>
  <!-- ── Account controls (DEV-923) ──────────────────────────────────────
       Organisation · user · theme, pinned under the last destination of the
       sidebar. They used to crowd the right of the TopBar and, on a phone,
       collapsed to three unlabelled icons floating over the module's own
       header; here they read in full, and the bar's right side is free for
       the module (see TopBar's `#metaship-topbar-actions`). In the desktop
       rail they shed their labels the same way the nav rows do. -->
  <div class="sh-foot" :class="{ 'sh-foot--rail': rail }">
    <p v-if="switchError" class="sh-foot__error">{{ switchError }}</p>

    <v-menu v-if="showOrgSwitcher" location="top start">
      <template #activator="{ props }">
        <button type="button" class="sh-foot__row sh-foot__org" :title="rail ? currentOrgName : undefined" v-bind="props">
          <LogoTile :src="currentOrgLogo" :alt="currentOrgName" :size="26" />
          <span v-if="!rail" class="sh-foot__label">{{ currentOrgName }}</span>
          <v-icon v-if="!rail" size="14" class="sh-foot__chev">unfold_more</v-icon>
        </button>
      </template>
      <div class="standalone-menu">
        <div class="standalone-menu__label">Switch organisation</div>
        <button
          v-for="org in session.accessibleOrgs"
          :key="org.id"
          type="button"
          class="sh-row"
          :class="{ 'sh-row--selected': org.id === session.currentOrg?.id }"
          :disabled="switching"
          @click="switchOrg(org)"
        >
          <LogoTile :src="org.logo || org.icon || null" :alt="org.name" :size="30" />
          <span class="sh-row__label">{{ org.name }}</span>
          <v-icon v-if="org.id === session.currentOrg?.id" size="16" color="primary">check</v-icon>
        </button>
      </div>
    </v-menu>
    <span
      v-else-if="session.currentOrg"
      class="sh-foot__row sh-foot__org sh-foot__org-static"
      :title="rail ? currentOrgName : undefined"
    >
      <LogoTile :src="currentOrgLogo" :alt="currentOrgName" :size="26" />
      <span v-if="!rail" class="sh-foot__label">{{ currentOrgName }}</span>
    </span>

    <div class="sh-foot__account">
      <!-- ── Who am I ────────────────────────────────────────────────────
           Name beside the avatar, not just an initial: a bare monogram is
           indistinguishable from the "?" fallback the shell used to show for
           every silently-resumed session (see stores/session.ts). -->
      <v-menu location="top start" @update:model-value="onUserMenu">
        <template #activator="{ props }">
          <button type="button" class="sh-foot__row sh-foot__user" :title="rail ? userFullName : undefined" v-bind="props">
            <span class="sh-foot__avatar">
              <img v-if="userAvatar" :src="userAvatar" :alt="userFullName" referrerpolicy="no-referrer" />
              <span v-else>{{ userInitials }}</span>
            </span>
            <span v-if="!rail && userFullName" class="sh-foot__label sh-foot__user-name">{{ userFirstName }}</span>
            <v-icon v-if="!rail" size="14" class="sh-foot__chev">unfold_more</v-icon>
          </button>
        </template>
        <div class="standalone-menu">
          <div class="standalone-menu__user">
            <span class="sh-foot__avatar sh-foot__avatar--lg">
              <img v-if="userAvatar" :src="userAvatar" :alt="userFullName" referrerpolicy="no-referrer" />
              <span v-else>{{ userInitials }}</span>
            </span>
            <span class="standalone-menu__user-id">
              <span class="standalone-menu__greeting">{{ greeting }}</span>
              <span class="standalone-menu__user-name">{{ userFullName || "Signed in" }}</span>
              <span class="standalone-menu__user-email">{{ session.user?.emailAddress }}</span>
            </span>
          </div>
          <div class="standalone-menu__divider"></div>
          <!-- Only while Chromium is holding an install prompt for this origin
               (src/pwa/install.ts) — an already-installed app, and every
               browser that does not fire the event, simply never shows it. -->
          <button v-if="canInstall" type="button" class="sh-row sh-foot__install" @click="install">
            <v-icon size="17" class="standalone-menu__row-icon">add_to_home_screen</v-icon>
            <span class="sh-row__label">Install app</span>
          </button>
          <button type="button" class="sh-row sh-foot__signout" @click="session.signOut()">
            <v-icon size="17" class="standalone-menu__row-icon">logout</v-icon>
            <span class="sh-row__label">Sign out</span>
          </button>
        </div>
      </v-menu>

      <button
        type="button"
        class="sh-foot__theme"
        :title="darkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        :aria-label="darkMode ? 'Switch to light mode' : 'Switch to dark mode'"
        @click="toggleTheme"
      >
        <Transition name="sh-icon-swap" mode="out-in">
          <v-icon :key="darkMode ? 'sun' : 'moon'" size="18">{{ darkMode ? "light_mode" : "dark_mode" }}</v-icon>
        </Transition>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useTheme } from "vuetify";
import { useSessionStore } from "@/stores/session";
import LogoTile from "./LogoTile.vue";
import { canInstall, promptInstall } from "@/pwa/install";
import type { UserOrganisation } from "@/tenant/types";

/** Time-of-day greeting for the user menu. Exported for the unit test. */
export function greetingFor(hour: number): string {
  if (hour < 5) return "Burning the midnight oil";
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  if (hour < 22) return "Good evening";
  return "Working late";
}

export default defineComponent({
  name: "SidebarFooter",
  components: { LogoTile },
  props: {
    /** Desktop icon rail — labels and chevrons drop, tooltips carry the names. */
    rail: { type: Boolean, default: false },
  },
  setup() {
    const session = useSessionStore();
    const theme = useTheme();
    const switching = ref(false);
    const switchError = ref<string | null>(null);

    const showOrgSwitcher = computed(
      () => session.tenant?.kind === "PRODUCT" && session.accessibleOrgs.length > 1,
    );
    const currentOrgName = computed(() => session.currentOrg?.name || "");
    const currentOrgLogo = computed(() => session.currentOrg?.logo || session.currentOrg?.icon || null);

    const darkMode = computed(() => theme.global.current.value.dark);
    function toggleTheme(): void {
      // The single source of truth for dark/light — flips body[data-theme]
      // too, which some hosted modules key off directly.
      window.MetaShipGlobalState?.toggleTheme();
      const isDark = window.MetaShipGlobalState?.getState().darkMode ?? true;
      theme.change(isDark ? "dark" : "light");
    }

    async function switchOrg(org: UserOrganisation): Promise<void> {
      if (org.id === session.currentOrg?.id || switching.value) return;
      switching.value = true;
      switchError.value = null;
      try {
        await session.selectOrganisation(org);
      } catch (e) {
        switchError.value = e instanceof Error ? e.message : "Could not switch organisation — please try again.";
      } finally {
        switching.value = false;
      }
    }

    /**
     * The menu opens with a word for the time of day, then the name — a
     * greeting is the smallest thing that makes the chrome feel like it knows
     * who is there.
     */
    const greeting = ref(greetingFor(new Date().getHours()));
    /** Re-read the clock each time the menu opens — a tab left open all day must not say "Good morning" at 15:00. */
    function onUserMenu(open: boolean): void {
      if (open) greeting.value = greetingFor(new Date().getHours());
    }

    const userFullName = computed(() => {
      const u = session.user;
      if (!u) return "";
      return `${u.firstname || ""} ${u.surname || ""}`.trim() || u.emailAddress || "";
    });
    const userFirstName = computed(() => session.user?.firstname || userFullName.value.split(" ")[0] || "");
    const userAvatar = computed(() => session.user?.avatar || null);
    const userInitials = computed(() => {
      const u = session.user;
      if (!u) return "?";
      const first = (u.firstname || u.emailAddress || "?").charAt(0);
      const second = (u.surname || "").charAt(0);
      return `${first}${second}`.toUpperCase();
    });

    return {
      session,
      showOrgSwitcher,
      currentOrgName,
      currentOrgLogo,
      switching,
      switchError,
      switchOrg,
      darkMode,
      toggleTheme,
      userFullName,
      userFirstName,
      userAvatar,
      userInitials,
      greeting,
      onUserMenu,
      canInstall,
      install: promptInstall,
    };
  },
});
</script>

<style scoped>
.sh-foot {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--sh-hairline);
}
.sh-foot__account {
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
}
.sh-foot--rail .sh-foot__account {
  flex-direction: column;
}

/* Same pill as a nav row — the footer is part of the sidebar, not a second
   toolbar bolted under it. */
.sh-foot__row {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  min-width: 0;
  height: 36px;
  padding: 0 6px;
  border: none;
  border-radius: var(--sh-radius);
  background: transparent;
  color: var(--sh-text);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    background var(--sh-motion) ease,
    transform var(--sh-motion) var(--sh-ease);
}
.sh-foot__row:hover {
  background: var(--sh-hover);
}
.sh-foot__row:active {
  transform: scale(0.985);
}
.sh-foot__org-static {
  cursor: default;
}
.sh-foot__org-static:hover {
  background: transparent;
}
.sh-foot--rail .sh-foot__row {
  justify-content: center;
  padding: 0;
  height: 40px;
}
.sh-foot__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sh-foot__chev {
  flex: 0 0 auto;
  color: var(--sh-text-45) !important;
}
.sh-foot__user {
  flex: 1 1 auto;
}

/* People are circles (brand marks are the white squares). */
.sh-foot__avatar {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  overflow: hidden;
  background: rgb(var(--v-theme-secondary));
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
/* The avatar gains a brand ring under the pointer, and the chevron beside
   it nudges up — "this opens something". */
.sh-foot__avatar {
  transition:
    box-shadow var(--sh-motion) ease,
    transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-foot__user:hover .sh-foot__avatar {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.45);
  transform: scale(1.06);
}
.sh-foot__chev {
  transition: transform var(--sh-motion) var(--sh-ease);
}
.sh-foot__row:hover .sh-foot__chev {
  transform: translateY(-1px);
}
.sh-foot__avatar--lg {
  width: 38px;
  height: 38px;
  font-size: 14px;
}
.sh-foot__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sh-foot__theme {
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: none;
  border-radius: var(--sh-radius);
  background: transparent;
  color: var(--sh-text-60);
  cursor: pointer;
  transition:
    background 150ms ease,
    color 150ms ease;
}
.sh-foot__theme:hover {
  background: var(--sh-hover);
  color: var(--sh-text);
}
/* The sun/moon tilts under the pointer, then turns over on the click
   (`sh-icon-swap`, standalone.css). */
.sh-foot__theme .v-icon {
  transition: transform var(--sh-motion-slow) var(--sh-ease-spring);
}
.sh-foot__theme:hover .v-icon {
  transform: rotate(-18deg) scale(1.1);
}
.sh-foot__theme:active {
  transform: scale(0.92);
}
.sh-foot--rail .sh-foot__theme {
  width: 40px;
  height: 40px;
}

.sh-foot__error {
  margin: 0 6px 4px;
  font-size: 11px;
  color: rgb(var(--v-theme-error));
}

/* ── Touch targets ─────────────────────────────────────────────────────
   Same breakpoint and reasoning as Sidebar.vue's rows. */
@media (max-width: 767.98px) {
  .sh-foot__row {
    height: 44px;
  }
  .sh-foot__label {
    font-size: 14px;
  }
  .sh-foot__theme {
    width: 44px;
    height: 44px;
  }
}
</style>

<!-- Menus teleport to <body>, outside this component's scope — these two
     surfaces are styled globally instead. -->
<style>
.standalone-menu {
  min-width: 230px;
  padding: 6px;
  border-radius: 12px;
  background: rgb(var(--v-theme-card));
  border: 1px solid var(--sh-hairline);
  box-shadow: 0 18px 40px -20px rgba(0, 0, 0, 0.6);
}
.standalone-menu__label {
  padding: 5px 8px 6px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
}
.standalone-menu__user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
}
.standalone-menu__greeting {
  display: block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 1px;
}
.standalone-menu__user-id {
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}
.standalone-menu__user-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--sh-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-menu__user-email {
  font-size: 11px;
  color: var(--sh-text-45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.standalone-menu__divider {
  height: 1px;
  margin: 5px 0;
  background: var(--sh-hairline);
}
.standalone-menu__row-icon {
  color: var(--sh-text-60) !important;
}
.standalone-menu .sh-row {
  height: 36px;
}
.standalone-menu .sh-row--selected {
  background: var(--sh-active);
}
</style>
